import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import DataTable from '@/components/data-table';
import ClearAllFiltersButton from '@/components/data-table-actions/ClearAllFiltersButton';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { removeObjectKeys } from '@/utils/utils';
import LocalQueryParamsProvider from '@/contexts/query-params/LocalQueryParamsProvider';
import { useProjectTasks } from '../../_shared/hooks';
import { TaskColumns } from './TaskColumns';
import { TASK_DEFAULT_PARAMS } from '../../_shared/constants';
import TaskDetailModal from './TaskDetailModal';
import TaskForm from './TaskForm';

function TaskListInner({
  projectId,
  members = [],
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  mutationLoading = false,
  canManage = false,
}) {
  const { t } = useTranslation(['forms', 'tables']);
  const { queryParams, setQueryParams } = useQueryParamsContext();

  const removeQueryParamsKeys = useMemo(
    () => removeObjectKeys(TASK_DEFAULT_PARAMS, ['limit', 'offset']),
    []
  );

  const handleFilterReset = useCallback(() => {
    setQueryParams(removeQueryParamsKeys);
  }, [setQueryParams, removeQueryParamsKeys]);

  const { data: tasksData, isLoading } = useProjectTasks(
    projectId,
    queryParams
  );
  const tasks = tasksData?.data || [];
  const totalTasks = tasksData?.count || 0;

  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);

  const handleAdd = () => {
    setEditingTask(null);
    setFormOpen(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormOpen(true);
  };

  const handleSubmitForm = (values) => {
    if (editingTask) {
      onUpdateTask(projectId, editingTask._id, values);
    } else {
      onAddTask(projectId, values);
    }
    setFormOpen(false);
    setEditingTask(null);
  };

  const columns = TaskColumns({
    canManage,
    onView: (task) => setViewingTask(task),
    onEdit: handleEdit,
    onDelete: onDeleteTask,
    projectId,
  });

  return (
    <>
      <DataTable
        data={tasks}
        total={totalTasks}
        title={`${t('tasks', { ns: 'forms' })} (${totalTasks})`}
        columns={columns}
        status={isLoading || mutationLoading ? 'pending' : 'success'}
        onChangePagination={(key, value) => setQueryParams({ [key]: value })}
        offset={queryParams.offset}
        limit={queryParams.limit}
        headerChildren={
          <>
            <ClearAllFiltersButton
              label={t('clearAllFilters', { ns: 'tables' })}
              onReset={handleFilterReset}
              defaultParams={removeQueryParamsKeys}
            />
            {canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                className="btn-primary !h-8 !border !border-white"
              >
                {t('addTask', { ns: 'forms' })}
              </Button>
            )}
          </>
        }
      />

      <TaskForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmitForm}
        initialValues={editingTask}
        members={members}
        isLoading={mutationLoading}
      />

      <TaskDetailModal
        open={!!viewingTask}
        onClose={() => setViewingTask(null)}
        task={viewingTask}
        canManage={canManage}
      />
    </>
  );
}

export default function TaskList(props) {
  return (
    <LocalQueryParamsProvider defaultParams={TASK_DEFAULT_PARAMS} prefix="t_">
      <TaskListInner {...props} />
    </LocalQueryParamsProvider>
  );
}
